import React from "react";
import styled from "styled-components";
import { getTheme } from "Theme";

const theme = getTheme();

// An animated on/off switch.
//   <ToggleSwitch label="Notify me" value={on} onChange={(next) => setOn(next)} />
// onChange gets the new value (true/false). It's a real button with
// role="switch", so the keyboard (Tab, Space, Enter) and screen readers work,
// and it's disabled by `disabled` or by a surrounding <fieldset disabled>.
// Clicking the label toggles it too.

const WIDTH = 44;
const HEIGHT = 24;
const GAP = 3;
const THUMB = HEIGHT - GAP * 2;

const Main = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  cursor: ${(props) => (props.$disabled ? "default" : "pointer")};
  user-select: none;
  font-weight: 700;
  opacity: ${(props) => (props.$disabled ? 0.55 : 1)};
`;

const Track = styled.button`
  position: relative;
  flex-shrink: 0;
  width: ${WIDTH}px;
  height: ${HEIGHT}px;
  padding: 0;
  border: none;
  border-radius: ${HEIGHT}px;
  background: ${(props) => (props.$on ? theme.MAIN_COLOR : theme.BUTTON_COLOR_GREY)};
  cursor: inherit;
  transition: background-color 200ms ease;

  &:focus-visible {
    outline: 3px solid ${theme.MAIN_COLOR};
    outline-offset: 2px;
  }

  &:disabled {
    cursor: default;
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

const Thumb = styled.span`
  position: absolute;
  top: ${GAP}px;
  left: ${GAP}px;
  width: ${THUMB}px;
  height: ${THUMB}px;
  border-radius: 50%;
  background: white;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
  transform: translateX(${(props) => (props.$on ? WIDTH - THUMB - GAP * 2 : 0)}px);
  transition: transform 200ms cubic-bezier(0.3, 1.4, 0.6, 1);

  ${Track}:active:not(:disabled) & {
    width: ${THUMB + 4}px;
    ${(props) => props.$on && `margin-left: -4px;`}
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

const ToggleSwitch = ({ label, value, onChange, disabled, name, className }) => {
  const on = !!value;
  return (
    <Main className={className} $disabled={disabled}>
      <Track
        type="button"
        role="switch"
        aria-checked={on}
        name={name}
        disabled={disabled}
        $on={on}
        onClick={() => onChange && onChange(!on)}
      >
        <Thumb $on={on} />
      </Track>
      {label}
    </Main>
  );
};

export default ToggleSwitch;
